import { router } from '@inertiajs/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Pencil, Plus } from 'lucide-react';

interface Recommendation {
  id: string;
  workflow_function_name: string;
  use_cases: string;
  keywords: string[];
  active: boolean;
}

interface IndexProps {
  recommendations: Recommendation[];
}

export default function Index({ recommendations }: IndexProps) {
  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this recommendation?')) {
      router.delete(`/dev/chat_recommendations/${id}`);
    }
  };

  const handleEdit = (id: string) => {
    router.visit(`/dev/chat_recommendations/${id}/edit`);
  };

  const handleNew = () => {
    router.visit('/dev/chat_recommendations/new');
  };

  return (
    <div className="container mx-auto p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Chat Recommendations</h1>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Recommendation
        </Button>
      </div>

      <p className="text-muted-foreground mb-6">
        Define use cases for workflows to help the chat assistant make better recommendations. You
        can also add recommendations from the workflow catalog page.
      </p>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Workflow</TableHead>
              <TableHead>Use Cases</TableHead>
              <TableHead>Keywords</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recommendations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No recommendations yet. Add one to help the chat assistant recommend workflows.
                </TableCell>
              </TableRow>
            ) : (
              recommendations.map((rec) => (
                <TableRow key={rec.id}>
                  <TableCell className="font-mono text-sm">{rec.workflow_function_name}</TableCell>
                  <TableCell className="max-w-md text-muted-foreground">
                    {rec.use_cases.slice(0, 100)}
                    {rec.use_cases.length > 100 && '...'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {rec.keywords?.slice(0, 3).map((kw) => (
                        <Badge key={kw} variant="secondary" className="text-xs">
                          {kw}
                        </Badge>
                      ))}
                      {rec.keywords?.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{rec.keywords.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={rec.active ? 'default' : 'secondary'}>
                      {rec.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(rec.id)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(rec.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
